import {
  endOfWeek,
  format,
  isSameMonth,
  isSameYear,
  parseISO,
  startOfWeek,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

export function changeTimeZone(
  date: string | number | Date,
  timeZone: string,
): Date {
  if (typeof date === "string" || typeof date === "number") {
    return new Date(
      new Date(date).toLocaleString("en-US", {
        timeZone,
      }),
    );
  }
  return new Date(
    date.toLocaleString("en-US", {
      timeZone,
    }),
  );
}

// This gets the week day by a date string on the specific client timezone.
export const getWeekDay = (dateString: string): string => {
  const date = parseISO(dateString);

  // Detect user's timezone
  const timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format the date to the user's timezone
  const formattedDate = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: timeZone,
  }).format(date);

  return formattedDate;
};

export function convertToUtc(date?: Date) {
  if (!date) {
    return;
  }
  const year = date.getFullYear();
  const month = convertToTwoDigits(date.getMonth() + 1);
  const day = convertToTwoDigits(date.getDate());
  const hours = convertToTwoDigits(date.getHours());
  const minutes = convertToTwoDigits(date.getMinutes());
  return new Date(`${year}-${month}-${day}T${hours}:${minutes}:00.000Z`);
}

export function convertToUtcString(date?: Date) {
  if (!date) {
    return;
  }
  const year = date.getUTCFullYear();
  const month = convertToTwoDigits(date.getUTCMonth() + 1);
  const day = convertToTwoDigits(date.getUTCDate());
  const hours = convertToTwoDigits(date.getUTCHours());
  const minutes = convertToTwoDigits(date.getUTCMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}:00.000Z`;
}

export function convertToLocal(date: Date, timezone: string) {
  if (!date) {
    return date;
  }
  const year = date.getFullYear();
  const month = convertToTwoDigits(date.getMonth() + 1);
  const day = convertToTwoDigits(date.getDate());
  const hours = convertToTwoDigits(date.getHours());
  const minutes = convertToTwoDigits(date.getMinutes());
  const offset = getOffset(date, timezone);
  return new Date(
    `${year}-${month}-${day}T${hours}:${minutes}:00.000${offset}`,
  );
}

const convertToTwoDigits = (nu: number) => {
  return nu !== null ? `0${nu}`.slice(-2) : null;
};

/**
 * Returns the offset of the date in the specified timezone
 * @param date - The date to get the offset of
 * @param timeZone - The timezone to get the offset of
 * @returns The offset of the date in the specified timezone
 * @example
 * getOffset(new Date(), "Europe/Oslo") // returns "+01:00"
 * getOffset(new Date(), "America/Los_Angeles") // returns "-08:00"
 */
export const getOffset = (date: Date, timeZone: string) => {
  return formatInTimeZone(date, timeZone, "xxx");
};

/*
 Removes the timezone from a date string by converting it to a UTC Date object.
 const dateString = '2023-05-08T05:00:00.000Z';
 const dateWithoutTimezone = removeTimezoneFromDateString(dateString);
 Removes the UTC timezone, and returns a new date with your timezone
 console.log(dateWithoutTimezone); // Output: Mon May 08 2023 05:00:00 GMT-0300 (Brasilia Standard Time) 
*/
export function removeTimezoneFromDateString(dateString?: string): Date {
  if (!dateString) return new Date();

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date string");
  }

  const options = { timeZone: "UTC" };
  return new Date(date.toLocaleString("en-US", options));
}

/**
 * Used to populate the date input or controller
 * @param isoDate string in the iso format, e.g. 2023-05-24T01:20:00.000-07:00
 * @returns a iso date string. e.g. 2023-05-24T01:20
 */
export function removeSecondsAndTimezone(isoDate?: string) {
  if (!isoDate) {
    return isoDate;
  }
  return isoDate.substring(0, 16);
}

export const DATE_INPUT_FORMAT = "yyyy-MM-dd";

export const TIME_INPUT_FORMAT = "hh:mm aa";

export const DATE_TIME_INPUT_FORMAT = "yyyy-MM-dd'T'h:mm aa";

export function getDateTitle({
  date,
  isMobile,
  view,
}: {
  date: Date;
  isMobile: boolean;
  view: "day" | "week" | "month" | "resource" | "listMonth";
}) {
  const yearFormat = isMobile ? "yy" : "yyyy";
  const monthFormat = isMobile ? "MMM" : "MMMM";
  if (view === "month" || view === "listMonth") {
    return format(date, `${monthFormat} ${yearFormat}`);
  }
  if (view === "day" || view === "resource") {
    return isMobile
      ? format(date, `${monthFormat} d`)
      : format(date, `${monthFormat} d, yyyy`);
  }
  const startOfWeekDate = startOfWeek(date);
  const endOfWeekDate = endOfWeek(date);
  const sameMonth = isSameMonth(startOfWeekDate, endOfWeekDate);
  const sameYear = isSameYear(startOfWeekDate, endOfWeekDate);
  const startMonth = format(startOfWeekDate, sameMonth ? monthFormat : "MMM");
  const startYear = !sameYear ? `${format(startOfWeekDate, yearFormat)} ` : "";
  const endMonth = format(endOfWeekDate, "MMM");
  const endYear = format(endOfWeekDate, yearFormat);
  return sameMonth
    ? `${startMonth} ${endYear}`
    : `${startMonth} ${startYear}- ${endMonth} ${endYear}`;
}
